import React from "react";
import Tooltip from "../../common/Tooltip";
import { matchPresence } from "./matchStatGroups";
// Shared /10 rating tier + formatting (single threshold source — the same
// utility the lineup component and TOTW use; Part D owns the wrappers).
import { ratingTier10, formatRating10 } from "../../totw/totwUtils";

// Empty rule (brief §1): a value the provider did not send renders NOTHING —
// the tile is dropped, never a dash. A 0 in a played match stays visible.
const isNil = (value) =>
  value === null || value === undefined || Number.isNaN(Number(value));

const PerformanceSummary = ({ selectedMatch }) => {
  const { played, inSquad } = matchPresence(selectedMatch);
  const ratingText = formatRating10(selectedMatch.statistics?.rating);

  const performanceData = [
    {
      title: "Goals",
      value: selectedMatch.statistics?.goals ?? null,
      icon: (
        <svg
          width="33"
          height="32"
          viewBox="0 0 33 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="0.199219"
            width="32"
            height="32"
            rx="8"
            fill="url(#paint0_linear_3270_52207)"
            fill-opacity="0.15"
          />
          <path
            d="M16.2005 24.3333C20.8029 24.3333 24.5339 20.6024 24.5339 16C24.5339 11.3976 20.8029 7.66667 16.2005 7.66667C11.5981 7.66667 7.86719 11.3976 7.86719 16C7.86719 20.6024 11.5981 24.3333 16.2005 24.3333Z"
            stroke="url(#paint1_linear_3270_52207)"
            stroke-width="1.65"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M16.2005 21C18.9619 21 21.2005 18.7614 21.2005 16C21.2005 13.2386 18.9619 11 16.2005 11C13.4391 11 11.2005 13.2386 11.2005 16C11.2005 18.7614 13.4391 21 16.2005 21Z"
            stroke="url(#paint2_linear_3270_52207)"
            stroke-width="1.65"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M16.2005 17.6667C17.121 17.6667 17.8672 16.9205 17.8672 16C17.8672 15.0795 17.121 14.3333 16.2005 14.3333C15.28 14.3333 14.5339 15.0795 14.5339 16C14.5339 16.9205 15.28 17.6667 16.2005 17.6667Z"
            stroke="url(#paint3_linear_3270_52207)"
            stroke-width="1.65"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <defs>
            <linearGradient
              id="paint0_linear_3270_52207"
              x1="16.1992"
              y1="0"
              x2="16.1992"
              y2="32"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#49BA62" />
              <stop offset="1" stop-color="#399D69" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_3270_52207"
              x1="16.2005"
              y1="7.66667"
              x2="16.2005"
              y2="24.3333"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#46B563" />
              <stop offset="1" stop-color="#3CA268" />
            </linearGradient>
            <linearGradient
              id="paint2_linear_3270_52207"
              x1="16.2005"
              y1="7.66667"
              x2="16.2005"
              y2="24.3333"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#46B563" />
              <stop offset="1" stop-color="#3CA268" />
            </linearGradient>
            <linearGradient
              id="paint3_linear_3270_52207"
              x1="16.2005"
              y1="7.66667"
              x2="16.2005"
              y2="24.3333"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#46B563" />
              <stop offset="1" stop-color="#3CA268" />
            </linearGradient>
          </defs>
        </svg>
      ),
    },
    {
      title: "Rating",
      // Rendered as the shared tier chip (see PerformanceCard); value keeps
      // the tile from collapsing when a rating exists.
      value: ratingText,
      isRating: true,
      icon: (
        <svg
          width="33"
          height="32"
          viewBox="0 0 33 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="0.398438"
            width="32"
            height="32"
            rx="8"
            fill="url(#paint0_linear_3270_52212)"
            fill-opacity="0.15"
          />
          <path
            d="M13.2395 17.575L12.2311 25.1667L16.3978 22.6667L20.5645 25.1667L19.5561 17.5667M22.2311 12.6667C22.2311 15.8883 19.6194 18.5 16.3978 18.5C13.1761 18.5 10.5645 15.8883 10.5645 12.6667C10.5645 9.445 13.1761 6.83333 16.3978 6.83333C19.6194 6.83333 22.2311 9.445 22.2311 12.6667Z"
            stroke="url(#paint1_linear_3270_52212)"
            stroke-width="1.65"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <defs>
            <linearGradient
              id="paint0_linear_3270_52212"
              x1="16.3984"
              y1="0"
              x2="16.3984"
              y2="32"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#4575F9" />
              <stop offset="1" stop-color="#4A4DEF" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_3270_52212"
              x1="16.3978"
              y1="6.83333"
              x2="16.3978"
              y2="25.1667"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#4571F8" />
              <stop offset="1" stop-color="#4953F0" />
            </linearGradient>
          </defs>
        </svg>
      ),
    },
    {
      title: "Minutes",
      value: selectedMatch.statistics?.minutes_played ?? null,
      icon: (
        <svg
          width="33"
          height="32"
          viewBox="0 0 33 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="0.599609"
            width="32"
            height="32"
            rx="8"
            fill="url(#paint0_linear_3270_52221)"
            fill-opacity="0.15"
          />
          <path
            d="M16.6009 11V16L19.9342 17.6667M24.9342 16C24.9342 20.6024 21.2033 24.3333 16.6009 24.3333C11.9985 24.3333 8.26758 20.6024 8.26758 16C8.26758 11.3976 11.9985 7.66667 16.6009 7.66667C21.2033 7.66667 24.9342 11.3976 24.9342 16Z"
            stroke="url(#paint1_linear_3270_52221)"
            stroke-width="1.65"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <defs>
            <linearGradient
              id="paint0_linear_3270_52221"
              x1="16.5996"
              y1="0"
              x2="16.5996"
              y2="32"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#A954E6" />
              <stop offset="1" stop-color="#CC378B" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_3270_52221"
              x1="16.6009"
              y1="7.66667"
              x2="16.6009"
              y2="24.3333"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#AD50DB" />
              <stop offset="1" stop-color="#C83A95" />
            </linearGradient>
          </defs>
        </svg>
      ),
    },
    {
      title: "Yellow Cards",
      value: selectedMatch.statistics?.yellow_cards ?? null,
      icon: (
        <svg
          width="33"
          height="32"
          viewBox="0 0 33 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="0.798828"
            width="32"
            height="32"
            rx="8"
            fill="url(#paint0_linear_3270_52230)"
            fill-opacity="0.15"
          />
          <rect
            x="9.79883"
            y="6"
            width="14"
            height="20"
            rx="4"
            fill="url(#paint1_linear_3270_52230)"
          />
          <defs>
            <linearGradient
              id="paint0_linear_3270_52230"
              x1="16.7988"
              y1="0"
              x2="16.7988"
              y2="32"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#F6B910" />
              <stop offset="1" stop-color="#F38113" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_3270_52230"
              x1="16.7988"
              y1="6"
              x2="16.7988"
              y2="26"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#F6B210" />
              <stop offset="1" stop-color="#F38513" />
            </linearGradient>
          </defs>
        </svg>
      ),
    },
    {
      title: "Red Cards",
      value: selectedMatch.statistics?.red_cards ?? null,
      icon: (
        <svg
          width="36"
          height="36"
          viewBox="0 0 36 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g filter="url(#filter0_d_3270_52239)">
            <rect
              x="2"
              width="32"
              height="32"
              rx="8"
              fill="url(#paint0_linear_3270_52239)"
              fill-opacity="0.15"
              shape-rendering="crispEdges"
            />
            <rect
              x="11"
              y="6"
              width="14"
              height="20"
              rx="4"
              fill="url(#paint1_linear_3270_52239)"
            />
          </g>
          <defs>
            <filter
              id="filter0_d_3270_52239"
              x="0"
              y="0"
              width="36"
              height="36"
              filterUnits="userSpaceOnUse"
              color-interpolation-filters="sRGB"
            >
              <feFlood flood-opacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset dy="2" />
              <feGaussianBlur stdDeviation="1" />
              <feComposite in2="hardAlpha" operator="out" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.04 0"
              />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow_3270_52239"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect1_dropShadow_3270_52239"
                result="shape"
              />
            </filter>
            <linearGradient
              id="paint0_linear_3270_52239"
              x1="18"
              y1="0"
              x2="18"
              y2="32"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#E43C42" />
              <stop offset="1" stop-color="#DA2144" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_3270_52239"
              x1="18"
              y1="6"
              x2="18"
              y2="26"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#E33843" />
              <stop offset="1" stop-color="#DB2344" />
            </linearGradient>
          </defs>
        </svg>
      ),
    },
  ];

  const PerformanceCard = ({ title, value, icon, isRating }) => {
    return (
      <div className="bg-white border border-[#E5E6E8] rounded-xl px-4 py-2 flex flex-col gap-2">
        <div className="flex items-center justify-between ">
          <p className="text-[#767A82] text-sm font-medium mb-1">{title}</p>
          {icon}
        </div>

        {isRating ? (
          // Shared /10 tier chip (ratingTier10 = the platform's single
          // threshold source): one decimal, tabular-nums, radius 6, 800.
          <p className="text-3xl">
            <span
              className="inline-block px-2 py-0.5 rounded-[6px] font-extrabold leading-tight"
              style={{
                backgroundColor: ratingTier10(value).bg,
                color: ratingTier10(value).fg,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {value}
            </span>
          </p>
        ) : (
          <p className="text-[#1A212F] font-bold text-3xl">{value}</p>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <svg
          width="20"
          height="12"
          viewBox="0 0 20 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18.4008 1.2L10.8008 8.8L6.80078 4.8L0.800781 10.8M18.4008 1.2H13.6008M18.4008 1.2V6"
            stroke="#2283FF"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>

        <h3 className="text-[#1A212F] font-bold text-lg">
          Performance Summary
        </h3>
        <Tooltip 
          content="Comprehensive match statistics and metrics" 
          position="bottom-right"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9.79883 13.2V10M9.79883 6.8H9.80683M17.7988 10C17.7988 14.4183 14.2171 18 9.79883 18C5.38055 18 1.79883 14.4183 1.79883 10C1.79883 5.58172 5.38055 2 9.79883 2C14.2171 2 17.7988 5.58172 17.7988 10Z"
              stroke="#A2A2A2"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </Tooltip>
      </div>

      {played ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4">
          {performanceData
            .filter((item) => !isNil(item.value))
            .map((item, index) => (
              <PerformanceCard key={item.title || index} {...item} />
            ))}
        </div>
      ) : (
        // 0-minutes case (brief §1) — two DIFFERENT quiet lines: in the squad
        // but never brought on, vs. not in the matchday squad at all.
        <div className="bg-white border border-[#E5E6E8] rounded-xl p-6 text-center">
          <p className="text-gray-500 text-sm">
            {inSquad ? "Not played · unused substitute" : "Not in squad"}
          </p>
        </div>
      )}
    </div>
  );
};

export default PerformanceSummary;
